import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import promptsService from '../api/promptsService';
import testConfigsService from '../api/testConfigsService';
import type { TestConfiguration, UserPrompt, UserPromptVersion } from '../types/types';

export const useTestConfigurations = () => {
    return useQuery<TestConfiguration[]>({
        queryKey: ['testConfigurations'],
        queryFn: () => testConfigsService.getAll(),
        refetchOnWindowFocus: false,
    } as UseQueryOptions<TestConfiguration[]>);
};

export const useUserPrompt = (promptId: number | undefined, userId?: number) => {
    return useQuery({
        queryKey: ['userPrompt', promptId, userId],
        queryFn: async () => {
            if (promptId == null) throw new Error('No promptId');
            return promptsService.getUserPrompt(promptId, userId);
        },
        enabled: promptId != null,
        refetchOnWindowFocus: false,
    });
};

export const useUserPrompts = (userId: number | undefined, testConfigurationId?: number) => {
    return useQuery<UserPrompt[]>({
        queryKey: ['userPrompts', userId, testConfigurationId],
        queryFn: async () => {
            if (userId == null) return [];
            return promptsService.getUserPrompts(userId, testConfigurationId);
        },
        enabled: userId != null,
        refetchOnWindowFocus: false,
    } as UseQueryOptions<UserPrompt[]>);
};

export const useUserPromptVersions = (promptId: number | undefined, userId?: number) => {
    return useQuery<UserPromptVersion[]>({
        queryKey: ['userPromptVersions', promptId, userId],
        queryFn: async () => {
            if (promptId == null) return [];
            return promptsService.getVersions(promptId, userId);
        },
        enabled: promptId != null,
        refetchOnWindowFocus: false,
    } as UseQueryOptions<UserPromptVersion[]>);
};

export const useUserPromptBranches = (promptId: number | undefined, userId?: number) => {
    return useQuery<UserPrompt[]>({
        queryKey: ['userPromptBranches', promptId, userId],
        queryFn: async () => {
            if (promptId == null) return [];
            return promptsService.getBranches(promptId, userId);
        },
        enabled: promptId != null,
        refetchOnWindowFocus: false,
    } as UseQueryOptions<UserPrompt[]>);
};

export const useAllPromptsForAdmin = (
    userId?: number,
    testConfigurationId?: number,
    page = 0,
    pageSize = 20,
) => {
    return useQuery<{ items: UserPrompt[]; total: number }>({
        queryKey: ['allPromptsAdmin', userId, testConfigurationId, page, pageSize],
        queryFn: () => promptsService.getAllPromptsForAdmin(userId, testConfigurationId, page, pageSize),
        refetchOnWindowFocus: false,
    } as UseQueryOptions<{ items: UserPrompt[]; total: number }>);
};

export const useSetCurrentVersion = (promptId: number, userId?: number) => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (versionId: number) =>
            promptsService.setCurrentVersion(promptId, versionId, userId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['userPrompt', promptId, userId] });
            qc.invalidateQueries({ queryKey: ['userPromptVersions', promptId, userId] });
            qc.invalidateQueries({ queryKey: ['allPromptsAdmin'] });
            qc.invalidateQueries({ queryKey: ['userPrompts'] });
        },
    });
};
